import { Component, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AdminConfigService } from '../../services/admin-config.service';
import { ToastNotificationComponent } from '../../toast-notification/toast-notification.component';
import { AcsLoc, AcsGroupListItem, AcsGroupDetail, AcsLocAssignment } from '../models/acs.models';
import { ACS_TOAST_SUCCESS_DURATION, ACS_TOAST_ERROR_DURATION } from '../utils/acs.constants';
import { extractErrorMessage, executeBulkDelete, formatBulkDeleteToast } from '../utils/acs.utils';
import { AcsBulkDeleteState } from '../utils/acs-bulk-delete-state';
import { AcsDropdownService } from '../utils/acs-dropdown.service';

@Component({
  selector: 'app-acs-trade-unit-groups',
  templateUrl: './acs-trade-unit-groups.component.html',
  styleUrls: ['../acs-shared.css', './acs-trade-unit-groups.component.css'],
  standalone: false
})
export class AcsTradeUnitGroupsComponent implements OnInit, OnDestroy {

  private _destroy$ = new Subject<void>();
  private _saveLocsTrigger$ = new Subject<void>();

  @ViewChild('toastNotification') toastNotification!: ToastNotificationComponent;

  activeSubTab: 'loc' | 'group' = 'loc';

  // ── Loc Management ──
  locs: AcsLoc[] = [];
  locFilter: string = '';
  locFilterSearchText = signal('');
  filteredLocList: AcsLoc[] = [];

  selectedLocIds: Set<string> = new Set();
  showBulkDeleteLocConfirm: boolean = false;
  isBulkDeletingLocs: boolean = false;

  pendingDeactivateLoc: AcsLoc | null = null;
  pendingDeactivateDate: string | null = null;
  previousDeactivateDate: string | null = null;
  showDeactivateConfirm: boolean = false;

  // ── Group Management ──
  groups: AcsGroupListItem[] = [];
  allLocs: AcsLocAssignment[] = [];
  selectedGroup: AcsGroupListItem | null = null;
  showForm: boolean = false;

  groupFilter: string = '';
  groupFilterSearchText = signal('');
  filteredGroupList: AcsGroupListItem[] = [];

  formGroupCode: string = '';
  formGroupName: string = '';
  formInactiveDate: string | null = null;
  assignedLocs: AcsLocAssignment[] = [];

  locSearchTerm: string = '';
  showLocDropdown: boolean = false;

  bulkDeleteGroups = new AcsBulkDeleteState<AcsGroupListItem>(g => g.groupId, () => this.filteredGroupList);

  selectedAssignedLocs: Set<string> = new Set();
  showRemoveLocConfirm: boolean = false;
  pendingLocSelections: Set<string> = new Set();

  constructor(
    private _adminConfigService: AdminConfigService,
    public dropdowns: AcsDropdownService
  ) {}

  ngOnInit(): void {
    this.loadLocs();
    this._adminConfigService.getScopedLocs().pipe(takeUntil(this._destroy$)).subscribe({
      next: (result) => this.allLocs = result.locs
    });
    this.loadGroups();
    fromEvent(document, 'click').pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.dropdowns.close();
      this.showLocDropdown = false;
      this.pendingLocSelections = new Set();
    });
    this._saveLocsTrigger$.pipe(debounceTime(500), takeUntil(this._destroy$)).subscribe(() => this.persistGroupLocAssignments());
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get locFilterDisplay(): string {
    if (!this.locFilter) return '-- All Trade Units --';
    const loc: AcsLoc | undefined = this.locs.find(l => l.locCode === this.locFilter);
    return loc ? `${loc.locCode} - ${loc.description}` : this.locFilter;
  }

  get filteredLocDropdownOptions(): AcsLoc[] {
    const term: string = this.locFilterSearchText();
    if (!term) return this.locs;
    const lower: string = term.toLowerCase();
    return this.locs.filter(l =>
      l.locCode.toLowerCase().includes(lower) || (l.description || '').toLowerCase().includes(lower)
    );
  }

  toggleLocFilterDropdown(): void {
    this.dropdowns.toggle('tu-loc-filter');
    if (this.dropdowns.isOpen('tu-loc-filter')) this.locFilterSearchText.set('');
  }

  selectLocFilterOption(loc: AcsLoc | null): void {
    this.locFilter = loc ? loc.locCode : '';
    this.dropdowns.close();
    this.locFilterSearchText.set('');
    this.applyLocFilter();
  }

  switchSubTab(tab: 'loc' | 'group'): void {
    this.activeSubTab = tab;
    this.selectedLocIds = new Set();
    this.bulkDeleteGroups.selectedIds = new Set();
    if (tab === 'loc') {
      this.showForm = false;
      this.selectedGroup = null;
    }
  }

  // ══════════════════════════════════════════
  // Loc Management
  // ══════════════════════════════════════════

  loadLocs(): void {
    this.selectedLocIds = new Set();
    this._adminConfigService.getLocs().pipe(takeUntil(this._destroy$)).subscribe({
      next: (data) => {
        this.locs = data;
        this.applyLocFilter();
      }
    });
  }

  applyLocFilter(): void {
    if (this.locFilter) {
      this.filteredLocList = this.locs.filter(l => l.locCode === this.locFilter);
    } else {
      this.filteredLocList = this.locs;
    }
  }

  isLocActive(loc: AcsLoc): boolean {
    return !loc.inactiveDate || new Date(loc.inactiveDate) > new Date();
  }

  requestDeactivateChange(loc: AcsLoc, newDate: string | null): void {
    this.pendingDeactivateLoc = loc;
    this.previousDeactivateDate = loc.inactiveDate;
    this.pendingDeactivateDate = newDate;
    this.showDeactivateConfirm = true;
  }

  cancelDeactivateChange(): void {
    if (this.pendingDeactivateLoc && this.previousDeactivateDate !== undefined) {
      this.pendingDeactivateLoc.inactiveDate = this.previousDeactivateDate;
    }
    this.pendingDeactivateLoc = null;
    this.pendingDeactivateDate = null;
    this.previousDeactivateDate = null;
    this.showDeactivateConfirm = false;
  }

  confirmDeactivateChange(): void {
    if (!this.pendingDeactivateLoc) return;
    this.pendingDeactivateLoc.inactiveDate = this.pendingDeactivateDate;
    this.saveLocDeactivation(this.pendingDeactivateLoc);
    this.pendingDeactivateLoc = null;
    this.pendingDeactivateDate = null;
    this.previousDeactivateDate = null;
    this.showDeactivateConfirm = false;
  }

  private saveLocDeactivation(loc: AcsLoc): void {
    this.saveLoc(loc, 'Deactivation date saved');
  }

  // --- Default Group ---

  groupDropdownSearchText = signal('');

  isGroupRowDropdownOpen(locCode: string): boolean {
    return this.dropdowns.isOpen('tu-default-group-' + locCode);
  }

  get filteredDefaultGroupOptions(): AcsGroupListItem[] {
    const term: string = this.groupDropdownSearchText();
    if (!term) return this.groups;
    const lower: string = term.toLowerCase();
    return this.groups.filter(g => g.groupCode.toLowerCase().includes(lower) || g.groupName.toLowerCase().includes(lower));
  }

  getDefaultGroupDisplay(loc: AcsLoc): string {
    if (!loc.defaultGroupId) return '';
    const group: AcsGroupListItem | undefined = this.groups.find(g => g.groupId === loc.defaultGroupId);
    return group ? group.groupCode : '';
  }

  toggleGroupDropdown(locCode: string): void {
    const id: string = 'tu-default-group-' + locCode;
    this.dropdowns.toggle(id);
    if (this.dropdowns.isOpen(id)) this.groupDropdownSearchText.set('');
  }

  selectDefaultGroup(loc: AcsLoc, group: AcsGroupListItem | null): void {
    loc.defaultGroupId = group ? group.groupId : null;
    this.dropdowns.close();
    this.groupDropdownSearchText.set('');
    this.saveLoc(loc, 'Default group saved');
  }

  private saveLoc(loc: AcsLoc, successMessage: string): void {
    const dto = {
      locCode: loc.locCode,
      company: loc.company,
      division: loc.division,
      description: loc.description,
      inactiveDate: loc.inactiveDate || null,
      defaultGroupId: loc.defaultGroupId || null
    };
    this._adminConfigService.saveLoc(dto).pipe(takeUntil(this._destroy$)).subscribe({
      next: () => this.toastNotification.showToast(successMessage, ACS_TOAST_SUCCESS_DURATION, 'success'),
      error: (err) => {
        const msg: string = extractErrorMessage(err, 'Failed to save');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
      }
    });
  }

  // --- Loc Bulk Delete ---

  get selectedLocCount(): number { return this.selectedLocIds.size; }
  isLocSelected(locCode: string): boolean { return this.selectedLocIds.has(locCode); }

  toggleLocSelect(locCode: string, event: Event): void {
    event.stopPropagation();
    if (this.selectedLocIds.has(locCode)) { this.selectedLocIds.delete(locCode); }
    else { this.selectedLocIds.add(locCode); }
    this.selectedLocIds = new Set(this.selectedLocIds);
  }

  get allVisibleLocsSelected(): boolean {
    return this.filteredLocList.length > 0 && this.filteredLocList.every(l => this.selectedLocIds.has(l.locCode));
  }

  toggleSelectAllLocs(): void {
    if (this.allVisibleLocsSelected) {
      for (const l of this.filteredLocList) { this.selectedLocIds.delete(l.locCode); }
    } else {
      for (const l of this.filteredLocList) { this.selectedLocIds.add(l.locCode); }
    }
    this.selectedLocIds = new Set(this.selectedLocIds);
  }

  requestBulkDeleteLocs(): void { this.showBulkDeleteLocConfirm = true; }
  cancelBulkDeleteLocs(): void { this.showBulkDeleteLocConfirm = false; }

  confirmBulkDeleteLocs(): void {
    this.isBulkDeletingLocs = true;
    const codes: string[] = [...this.selectedLocIds];
    executeBulkDelete(codes, (code: string) => this._adminConfigService.deleteLoc(code))
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (result) => {
          const toast = formatBulkDeleteToast(result, 'trade unit');
          this.toastNotification.showToast(toast.message, toast.type === 'success' ? ACS_TOAST_SUCCESS_DURATION : ACS_TOAST_ERROR_DURATION, toast.type);
          this.selectedLocIds = new Set();
          this.showBulkDeleteLocConfirm = false;
          this.isBulkDeletingLocs = false;
          this.loadLocs();
        }
      });
  }

  // ══════════════════════════════════════════
  // Group Management
  // ══════════════════════════════════════════

  get groupFilterDisplay(): string {
    if (!this.groupFilter) return '-- All Groups --';
    const group: AcsGroupListItem | undefined = this.groups.find(g => g.groupCode === this.groupFilter);
    return group ? `${group.groupCode} - ${group.groupName}` : this.groupFilter;
  }

  get filteredGroupDropdownOptions(): AcsGroupListItem[] {
    const term: string = this.groupFilterSearchText();
    if (!term) return this.groups;
    const lower: string = term.toLowerCase();
    return this.groups.filter(g =>
      g.groupCode.toLowerCase().includes(lower) || (g.groupName || '').toLowerCase().includes(lower)
    );
  }

  toggleGroupFilterDropdown(): void {
    this.dropdowns.toggle('tu-group-filter');
    if (this.dropdowns.isOpen('tu-group-filter')) this.groupFilterSearchText.set('');
  }

  selectGroupFilterOption(group: AcsGroupListItem | null): void {
    this.groupFilter = group ? group.groupCode : '';
    this.dropdowns.close();
    this.groupFilterSearchText.set('');
    this.applyGroupFilter();
  }

  applyGroupFilter(): void {
    if (this.groupFilter) {
      this.filteredGroupList = this.groups.filter(g => g.groupCode === this.groupFilter);
    } else {
      this.filteredGroupList = this.groups;
    }
  }

  loadGroups(): void {
    this.bulkDeleteGroups.selectedIds = new Set();
    this._adminConfigService.getGroups().pipe(takeUntil(this._destroy$)).subscribe({
      next: (data) => {
        this.groups = data;
        this.applyGroupFilter();
      }
    });
  }

  selectGroup(group: AcsGroupListItem): void {
    this.selectedGroup = group;
    this.showForm = true;
    this.selectedAssignedLocs = new Set();
    this._adminConfigService.getGroupById(group.groupId).pipe(takeUntil(this._destroy$)).subscribe({
      next: (detail: AcsGroupDetail) => {
        this.formGroupCode = detail.groupCode || '';
        this.formGroupName = detail.groupName || '';
        this.formInactiveDate = detail.inactiveDate ? detail.inactiveDate.substring(0, 10) : null;
        this.assignedLocs = (detail.locCodes || []).map((code: string) => {
          const match = this.allLocs.find(l => l.locCode === code);
          return { locCode: code, description: match?.description || '' };
        }).sort((a, b) => a.locCode.localeCompare(b.locCode));
      }
    });
  }

  addNewGroup(): void {
    this.selectedGroup = null;
    this.showForm = true;
    this.formGroupCode = '';
    this.formGroupName = '';
    this.formInactiveDate = null;
    this.assignedLocs = [];
    this.selectedAssignedLocs = new Set();
    this.locSearchTerm = '';
    this.showLocDropdown = false;
  }

  get isEditingGroup(): boolean {
    return this.selectedGroup !== null;
  }

  get filteredLocs(): AcsLocAssignment[] {
    const assignedSet = new Set(this.assignedLocs.map(l => l.locCode));
    let items = this.allLocs.filter(l => !assignedSet.has(l.locCode));
    if (this.locSearchTerm) {
      const term = this.locSearchTerm.toLowerCase();
      items = items.filter(l =>
        l.locCode.toLowerCase().includes(term) || (l.description || '').toLowerCase().includes(term)
      );
    }
    return items;
  }

  togglePendingLoc(loc: AcsLocAssignment): void {
    if (this.pendingLocSelections.has(loc.locCode)) {
      this.pendingLocSelections.delete(loc.locCode);
    } else {
      this.pendingLocSelections.add(loc.locCode);
    }
    this.pendingLocSelections = new Set(this.pendingLocSelections);
  }

  isPendingLoc(locCode: string): boolean { return this.pendingLocSelections.has(locCode); }
  get pendingLocCount(): number { return this.pendingLocSelections.size; }

  addPendingLocsToGroup(): void {
    for (const locCode of this.pendingLocSelections) {
      if (this.assignedLocs.some(l => l.locCode === locCode)) continue;
      const loc = this.allLocs.find(l => l.locCode === locCode);
      if (loc) this.assignedLocs.push({ locCode: loc.locCode, description: loc.description });
    }
    this.assignedLocs.sort((a, b) => a.locCode.localeCompare(b.locCode));
    this.pendingLocSelections = new Set();
    this.selectedAssignedLocs = new Set();
    this.locSearchTerm = '';
    this.showLocDropdown = false;
    this._saveLocsTrigger$.next();
  }

  // --- Assigned Loc Selection & Remove ---

  get selectedAssignedLocCount(): number { return this.selectedAssignedLocs.size; }
  isAssignedLocSelected(locCode: string): boolean { return this.selectedAssignedLocs.has(locCode); }

  toggleAssignedLocSelect(locCode: string): void {
    if (this.selectedAssignedLocs.has(locCode)) { this.selectedAssignedLocs.delete(locCode); }
    else { this.selectedAssignedLocs.add(locCode); }
    this.selectedAssignedLocs = new Set(this.selectedAssignedLocs);
  }

  get allAssignedLocsSelected(): boolean {
    return this.assignedLocs.length > 0 && this.assignedLocs.every(l => this.selectedAssignedLocs.has(l.locCode));
  }

  toggleSelectAllAssignedLocs(): void {
    if (this.allAssignedLocsSelected) {
      for (const l of this.assignedLocs) { this.selectedAssignedLocs.delete(l.locCode); }
    } else {
      for (const l of this.assignedLocs) { this.selectedAssignedLocs.add(l.locCode); }
    }
    this.selectedAssignedLocs = new Set(this.selectedAssignedLocs);
  }

  requestRemoveAssignedLocs(): void { this.showRemoveLocConfirm = true; }
  cancelRemoveAssignedLocs(): void { this.showRemoveLocConfirm = false; }

  confirmRemoveAssignedLocs(): void {
    this.assignedLocs = this.assignedLocs.filter(l => !this.selectedAssignedLocs.has(l.locCode));
    this.selectedAssignedLocs = new Set();
    this.showRemoveLocConfirm = false;
    this._saveLocsTrigger$.next();
  }

  onLocSearchBlur(): void {
    setTimeout(() => {
      if (this.pendingLocSelections.size === 0) this.showLocDropdown = false;
    }, 150);
  }

  onGroupCodeInput(): void {
    this.formGroupCode = (this.formGroupCode || '').toUpperCase().substring(0, 10);
  }

  onGroupNameInput(): void {
    this.formGroupName = (this.formGroupName || '').substring(0, 50);
  }

  private persistGroupLocAssignments(): void {
    if (!this.selectedGroup) return;
    const dto = {
      groupId: this.selectedGroup.groupId,
      groupCode: this.formGroupCode,
      groupName: this.formGroupName,
      inactiveDate: this.formInactiveDate || null,
      locCodes: this.assignedLocs.map(l => l.locCode)
    };
    this._adminConfigService.saveGroup(dto).pipe(takeUntil(this._destroy$)).subscribe({
      next: () => this.loadGroups(),
      error: (err) => {
        const msg: string = extractErrorMessage(err, 'Failed to save trade unit assignments');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
      }
    });
  }

  saveGroup(): void {
    if (!this.formGroupCode?.trim() || !this.formGroupName?.trim()) {
      this.toastNotification.showToast('Group Code and Group Name are required', ACS_TOAST_SUCCESS_DURATION, 'error');
      return;
    }
    const dto = {
      groupId: this.selectedGroup?.groupId || 0,
      groupCode: this.formGroupCode.toUpperCase().substring(0, 10),
      groupName: this.formGroupName.substring(0, 50),
      inactiveDate: this.formInactiveDate || null,
      locCodes: this.assignedLocs.map(l => l.locCode)
    };
    this._adminConfigService.saveGroup(dto).pipe(takeUntil(this._destroy$)).subscribe({
      next: (result) => {
        if (result) {
          this.toastNotification.showToast('Group saved', ACS_TOAST_SUCCESS_DURATION, 'success');
          this.showForm = false;
          this.selectedGroup = null;
          this.loadGroups();
        }
      },
      error: (err) => {
        const msg: string = extractErrorMessage(err, 'Failed to save group');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
      }
    });
  }

  isGroupActive(group: AcsGroupListItem): boolean {
    return !group.inactiveDate || new Date(group.inactiveDate) > new Date();
  }

  clearGroupInactiveDate(): void {
    this.formInactiveDate = null;
  }

  cancelGroupForm(): void {
    this.showForm = false;
    this.selectedGroup = null;
  }

  // --- Group Bulk Delete ---

  confirmBulkDeleteGroups(): void {
    this.bulkDeleteGroups.isDeleting = true;
    const ids: number[] = [...this.bulkDeleteGroups.selectedIds];
    executeBulkDelete(ids, (id: number) => this._adminConfigService.deleteGroup(id))
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (result) => {
          const toast = formatBulkDeleteToast(result, 'group');
          this.toastNotification.showToast(toast.message, toast.type === 'success' ? ACS_TOAST_SUCCESS_DURATION : ACS_TOAST_ERROR_DURATION, toast.type);
          if (this.selectedGroup && this.bulkDeleteGroups.selectedIds.has(this.selectedGroup.groupId)) {
            this.showForm = false;
            this.selectedGroup = null;
          }
          this.bulkDeleteGroups.reset();
          this.loadGroups();
        }
      });
  }

}
